import { combineLatest, Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { environment } from './../../../../environments/environment';
import { Farm, Field, Payment, StoreModel } from '../../_models';
import { SatelliteIndexes } from '../../../shared/satellite-select-index/indexes';
import { SatelliteIndexesMaster } from './../../../shared/satellite-select-index/indexes';
import { StoreModelService } from './../storeModel.service';
import { SM } from './../../../../assets/data/enum/storeModel.enum';
import { AppState } from '../../_reducers';
import { selectCurrentFarm } from '../../_selectors/currentFarm.selectors';
import { selectAllPayments } from '../../_selectors/payment.selectors';
import { SatelliteField } from '../../_models/satelliteField';

export const Provider = {
  SPACESENSE: 'SPACESENSE',
  AGROSAT: 'AGROSAT',
};
@Injectable()
export class SatelliteService {
  private apiBaseUrl = environment.apiBaseUrl;
  satelliteIndexSelected: BehaviorSubject<string> = new BehaviorSubject<string>('ndvi');
  satelliteDateSelected: BehaviorSubject<string> = new BehaviorSubject<string>('');
  satelliteIndexDates: BehaviorSubject<Map<string, Array<any>>> = new BehaviorSubject<
    Map<string, Array<any>>
  >(new Map<string, Array<any>>());
  satelliteFieldList: BehaviorSubject<Map<string, any>> = new BehaviorSubject<Map<string, any>>(
    new Map<string, Array<any>>()
  );
  imageByDates: BehaviorSubject<Map<string, any>> = new BehaviorSubject<Map<string, any>>(
    new Map<string, any>()
  );
  imageByLastDates: BehaviorSubject<Map<string, any>> = new BehaviorSubject<Map<string, any>>(
    new Map<string, any>()
  );
  idByIndex: BehaviorSubject<Map<string, any>> = new BehaviorSubject<Map<string, any>>(
    new Map<string, any>()
  );
  prescriptionMap: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  opacityPrescriptionMap: BehaviorSubject<any> = new BehaviorSubject<any>(1);
  fieldList: Observable<Field[]>;
  passThroughFieldActivities: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  prescriptionFieldId: BehaviorSubject<any> = new BehaviorSubject<any>(0);
  satelliteAllFieldsWithDateAndIndex: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  satelliteAllDateByIndex: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  fieldListBySatellite = [];
  paymentList$: Observable<Payment[]>;
  externalProviderPng$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  farm$: Observable<Farm>;
  wordKey = 'BARILLA';
  provider = Provider.SPACESENSE;
  storeModels: Array<StoreModel> = [];
  SM;

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private storeModelService: StoreModelService
  ) {
    this.paymentList$ = this.store.select(selectAllPayments);
    this.farm$ = this.store.select(selectCurrentFarm);
    this.storeModelService.list.subscribe((res) => (this.storeModels = res));
  }

  setIndexProvider() {
    combineLatest([this.farm$, this.storeModelService.list]).subscribe(([f, storeModels]) => {
      if (f && f.id && storeModels && storeModels.length > 0) {
        const storeModel = storeModels.find((sModel: StoreModel) => {
          return sModel.roleGranted === SM.SM_PATHOGEN_AGROSAT;
        });

        const indexes = JSON.parse(JSON.stringify(SatelliteIndexesMaster.colormaps));

        if (
          storeModel &&
          storeModel.roleGranted &&
          storeModel.expirationDate !== null &&
          !this.storeModelService.isExpired(moment(storeModel.expirationDate as any))
        ) {
          this.provider = Provider.AGROSAT;
          SatelliteIndexes.colormaps = indexes.filter((e) => {
            return e.providers.includes('agrosat');
          });
        } else {
          this.provider = Provider.SPACESENSE;
          SatelliteIndexes.colormaps = indexes.filter((e) => {
            return e.providers.includes('spacesense');
          });
        }
      }
    });
  }

  getFieldsWithDateAndIndex() {
    this.getAllFieldsWithDateAndIndex().subscribe((fieldList) => {
      this.fieldListBySatellite = fieldList;
      this.satelliteAllFieldsWithDateAndIndex.next(fieldList);
    });
  }

  getDateByIndex(index) {
    this.getAllDateByIndex(index).subscribe((dateList) => {
      this.satelliteAllDateByIndex.next(dateList);
    });
  }

  getUpdateStateFieldActivities = () => {
    return this.passThroughFieldActivities.asObservable();
  };
  updateStateFieldActivities(bool: boolean) {
    this.passThroughFieldActivities.next(bool);
  }
  getFieldIdPrescriptionMap = () => {
    return this.prescriptionFieldId.asObservable();
  };
  updateFieldIdPrescription(id) {
    this.prescriptionFieldId.next(id);
  }

  getSatelliteFields(index): Observable<any[]> {
    return this.http
      .get<any[]>(this.apiBaseUrl + 'api/private/v1/satellite/fields?index=' + index)
      .pipe(map((response) => response));
  }

  getSatelliteDatesByFieldId(index: string, fieldId: number): any[] {
    if (!this.fieldListBySatellite || this.fieldListBySatellite.length === 0) {
      return [];
    }

    const field = this.fieldListBySatellite.find((item) => item.field.id === Number(fieldId));

    const metrics = field?.metricsByIndex?.[index];

    if (metrics && metrics.length > 0) {
      return metrics;
    } else {
      return [];
    }
  }

  getMetricsByFieldId(index, date, field): Observable<any[]> {
    date = date.replace(':00Z', ':00.000Z');
    return this.http
      .get<
        any[]
      >(this.apiBaseUrl + 'api/private/v1/satellite/fields/' + field + '/images/metrics?index=' + index + '&date=' + date)
      .pipe(map((response) => response));
  }

  getAllDateByIndex(index): Observable<any[]> {
    return this.http
      .get<any[]>(this.apiBaseUrl + 'api/private/v3/satellite/fields/dates?index=' + index)
      .pipe(map((response) => response));
  }

  getAllFieldsWithDateAndIndex(): Observable<any[]> {
    return this.http
      .get<any[]>(this.apiBaseUrl + 'api/private/v1/satellite/fieldSatelliteMetricsByIndex')
      .pipe(map((response) => response));
  }

  processRasterImage(id): Observable<any[]> {
    return this.http
      .post<any[]>(this.apiBaseUrl + 'api/private/v1/mapShape/processRasterImage/' + id, [])
      .pipe(map((response) => response));
  }

  uploadRasterImage(id, cmap, minVal, maxVal): Observable<any[]> {
    const min = minVal ? '&minVal=' + minVal : '';
    const max = maxVal ? '&maxVal=' + maxVal : '';
    const colorMap = cmap && cmap !== '' ? cmap : 'RdYlGn';
    return this.http
      .post<
        any[]
      >(this.apiBaseUrl + 'api/private/v1/satellite/rasterImage/uploadAsync/' + id + '?cmap=' + colorMap + min + max, [])
      .pipe(map((response) => response));
  }

  handleFileSelect(file) {
    // tslint:disable-next-line:prefer-const
    const reader = new FileReader();

    reader.onload = this._handleReaderLoaded.bind(this);

    return reader.readAsBinaryString(file);
  }

  _handleReaderLoaded(readerEvt) {
    // tslint:disable-next-line:prefer-const
    const binaryString = readerEvt.target.result;
    ////  console.log(btoa(binaryString));
    return btoa(binaryString);
  }

  // prescription/step1?type=n-fertilizer-zones&index=ndvi&date=2020-05-05T00:00:00.000Z&numZones=2

  getPrescriptionMapStep1(field, type, index, date, zoneNumber, nitroUnit, nitroPerc) {
    if (!date.includes('.000Z')) {
      date = date.replace(':00Z', ':00.000Z');
    }
    // tslint:disable-next-line: max-line-length
    return this.http
      .post(
        this.apiBaseUrl +
          'api/private/v1/satellite/fields/' +
          field +
          '/prescription/step1?type=' +
          type +
          '&index=' +
          index +
          '&date=' +
          date +
          '&numZones=' +
          zoneNumber +
          '&nitroUnit=' +
          nitroUnit +
          '&nitroPerc=' +
          nitroPerc,
        []
      )
      .pipe(map((response) => response));
  }

  postPrescriptionMapStep2(field, bodyForPrescription): Observable<any> {
    // tslint:disable-next-line: max-line-length
    return this.http
      .post(
        this.apiBaseUrl + 'api/private/v1/satellite/fields/' + field + '/prescription/step2',
        bodyForPrescription
      )
      .pipe(map((response) => response));
  }

  postGeoJsonToShape(formatFile, bodyForPrescription) {
    return this.http
      .post(
        this.apiBaseUrl + 'api/private/v1/satellite/geojsonToShape?format=' + formatFile,
        bodyForPrescription
      )
      .pipe(map((response) => response));
  }

  getColorByCountZone = (countZone, idZone): string => {
    // tslint:disable-next-line:prefer-const
    const idZoneCorrect = idZone - 1;
    let colorZones = [];
    if (countZone === 1) {
      colorZones = ['#198012'];
    } else if (countZone === 2) {
      colorZones = ['#de2f12', '#198012'];
    } else if (countZone === 3) {
      colorZones = ['#de2f12', '#f6fe28', '#198012'];
    } else if (countZone === 4) {
      colorZones = ['#de2f12', '#d6861a', '#f6fe28', '#198012'];
    } else if (countZone === 5) {
      colorZones = ['#de2f12', '#d6861a', '#f6fe28', '#75c715', '#198012'];
    } else if (countZone === 6) {
      colorZones = ['#de2f12', '#d6861a', '#f6fe28', '#75c715', '#31a012', '#198012'];
    } else if (countZone === 7) {
      colorZones = ['#de2f12', '#d6861a', '#f6fe28', '#75c715', '#31a012', '#32a114', '#198012'];
    }
    return colorZones[idZoneCorrect];
  };

  changeImage = (randomId, image, fieldId, indexSelect) => {
    const id = randomId + '-' + indexSelect + '-' + fieldId.toString();
    // tslint:disable-next-line:prefer-const
    const pngElement = document.getElementById(id);
    if (pngElement) {
      // tslint:disable-next-line:no-unused-expression
      (<HTMLImageElement>document.getElementById(id)).getElementsByTagName('img')[0].src = image;
    }
  };

  getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  isExiperd = (expirationDate): boolean => {
    const currentTimeStamp = moment().utc();
    const result = expirationDate.diff(currentTimeStamp, 'days');
    if (result > 0) {
      return false;
    } else if (result < 0) {
      return true;
    } else {
      return false;
    }
  };

  getSatelliteUpdates(data: any = null): Observable<SatelliteField[]> {
    let simbol = '';
    let queryString = '';
    if (data && data.index) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'index=' + data.index;
    }
    if (data && data.field) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'field=' + data.field;
    }
    if (data && data.from) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'from=' + data.from;
    }
    if (data && data.to) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'to=' + data.to;
    }

    if (data && data.excludes && data.excludes.length > 0) {
      data.excludes.forEach((exclude) => {
        simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
        queryString = queryString + simbol + 'excludes=' + exclude;
      });
    }
    return this.http
      .get<SatelliteField[]>(this.apiBaseUrl + 'api/private/v1/satelliteUpdates' + queryString)
      .pipe(
        map((response) => {
          if (response) {
            //this.satelliteFiledList$.next(response.json());
            return response;
          }
        })
      );
  }
}
